import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import deLocale from "@fullcalendar/core/locales/de";
import { useConfirm } from "../contexts/ConfirmContext";
import { useNavigate } from "react-router-dom";
import { Plus, X, Trash2, Calendar as CalIcon, MapPin, Users, ExternalLink, Tag, Repeat } from "lucide-react";
import "../styles/calendar.css";

const CATEGORIES = [
  { value: "onsite", label: "Vor-Ort", color: "#2563eb" },
  { value: "remote", label: "Remote", color: "#0ea5e9" },
  { value: "maintenance", label: "Wartung", color: "#f59e0b" },
  { value: "meeting", label: "Meeting", color: "#8b5cf6" },
  { value: "vacation", label: "Urlaub / Abwesenheit", color: "#10b981" },
  { value: "other", label: "Sonstiges", color: "#64748b" },
];

const RECURRENCE = [
  { value: "", label: "Einmalig" },
  { value: "daily", label: "Täglich" },
  { value: "weekly", label: "Wöchentlich" },
  { value: "monthly", label: "Monatlich" },
];

const RECURRENCE_DAYS = { daily: 1, weekly: 7, monthly: 30 };

// Expand recurrence to ~6 months of instances on the client (for FullCalendar display)
function expandEvents(events, horizonMonths = 6) {
  const out = [];
  const horizon = new Date();
  horizon.setMonth(horizon.getMonth() + horizonMonths);

  for (const e of events) {
    if (!e.recurrence) {
      out.push(e);
      continue;
    }
    const stepDays = RECURRENCE_DAYS[e.recurrence];
    if (!stepDays) { out.push(e); continue; }
    const startDate = new Date(e.start);
    const endDate = e.end ? new Date(e.end) : null;
    const duration = endDate ? endDate - startDate : 0;
    let occ = 0;
    while (true) {
      const newStart = new Date(startDate);
      if (e.recurrence === "monthly") {
        newStart.setMonth(newStart.getMonth() + occ);
      } else {
        newStart.setDate(newStart.getDate() + occ * stepDays);
      }
      if (newStart > horizon) break;
      const newEnd = duration ? new Date(newStart.getTime() + duration) : null;
      out.push({
        ...e,
        _id: `${e._id}__occ${occ}`,
        _origId: e._id,
        _isOccurrence: occ > 0,
        start: newStart.toISOString(),
        end: newEnd ? newEnd.toISOString() : undefined,
      });
      occ += 1;
      if (occ > 200) break; // safety
    }
  }
  return out;
}

export default function CalendarPage() {
  const confirm = useConfirm();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterAttendee, setFilterAttendee] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "",
    start: "", end: "", all_day: false,
    location: "", customer_id: "", attendees: [],
    category: "meeting", recurrence: "",
  });
  const calendarRef = useRef();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, u, c] = await Promise.all([
        api.get("/events"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
      ]);
      setEvents(e.data);
      setUsers(u.data);
      setCustomers(c.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = events.filter(e => {
    if (filterCategory && e.category !== filterCategory) return false;
    if (filterAttendee && !(e.attendees || []).includes(filterAttendee)) return false;
    return true;
  });

  const fcEvents = expandEvents(filtered).map(e => ({
    id: e._id,
    title: e.title,
    start: e.start,
    end: e.end || undefined,
    allDay: e.all_day,
    backgroundColor: e.color || CATEGORIES.find(c => c.value === e.category)?.color || "#64748b",
    borderColor: "transparent",
    extendedProps: e,
  }));

  // ── Form helpers ──────────────────────────────────────────────────────
  const openCreate = (slot) => {
    setEditingId(null);
    const start = slot?.start ? new Date(slot.start) : new Date();
    const end = slot?.end ? new Date(slot.end) : new Date(start.getTime() + 60 * 60 * 1000);
    setForm({
      title: "", description: "",
      start: toLocalInput(start), end: toLocalInput(end),
      all_day: !!slot?.allDay, location: "", customer_id: "", attendees: [],
      category: "meeting", recurrence: "",
    });
    setShowForm(true);
  };

  const openEdit = (e) => {
    const ev = e.extendedProps?._origId
      ? events.find(x => x._id === e.extendedProps._origId)
      : events.find(x => x._id === e.id) || e.extendedProps;
    if (!ev) return;
    setEditingId(ev._id);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      start: toLocalInput(new Date(ev.start)),
      end: ev.end ? toLocalInput(new Date(ev.end)) : "",
      all_day: !!ev.all_day,
      location: ev.location || "",
      customer_id: ev.customer_id || "",
      attendees: ev.attendees || [],
      category: ev.category || "meeting",
      recurrence: ev.recurrence || "",
    });
    setShowForm(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const payload = {
      ...form,
      start: new Date(form.start).toISOString(),
      end: form.end ? new Date(form.end).toISOString() : null,
      customer_id: form.customer_id || null,
      recurrence: form.recurrence || null,
    };
    if (editingId) {
      const r = await api.put(`/events/${editingId}`, payload);
      setEvents(events.map(e => e._id === editingId ? r.data : e));
    } else {
      const r = await api.post("/events", payload);
      setEvents([r.data, ...events]);
    }
    setShowForm(false);
    setEditingId(null);
  };

  const remove = async () => {
    if (!editingId) return;
    if (!(await confirm({ title: "Termin löschen", message: "Dieser Termin wird dauerhaft gelöscht.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/events/${editingId}`);
    setEvents(events.filter(e => e._id !== editingId));
    setShowForm(false);
    setEditingId(null);
  };

  // Drag&Drop / resize handlers
  const handleEventDrop = async (info) => {
    const origId = info.event.extendedProps?._origId || info.event.id;
    const target = events.find(x => x._id === origId);
    if (!target) return info.revert();
    if (info.event.extendedProps?._isOccurrence) {
      // We don't allow moving a single occurrence of a series — revert.
      info.revert();
      window.alert("Wiederkehrende Termine können nur als Serie verschoben werden. Klicke auf den Termin, um ihn zu bearbeiten.");
      return;
    }
    const payload = {
      start: info.event.start.toISOString(),
      end: info.event.end ? info.event.end.toISOString() : null,
      all_day: info.event.allDay,
    };
    try {
      const r = await api.put(`/events/${origId}`, payload);
      setEvents(prev => prev.map(e => e._id === origId ? r.data : e));
    } catch {
      info.revert();
    }
  };

  const handleEventClick = (info) => openEdit(info.event);
  const handleSelect = (info) => openCreate(info);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team-Kalender</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} Termine — Drag &amp; Drop zum Verschieben, Klick zum Bearbeiten
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            data-testid="cal-filter-category"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Kategorien</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select
            data-testid="cal-filter-attendee"
            value={filterAttendee}
            onChange={e => setFilterAttendee(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Teilnehmer</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.initials})</option>)}
          </select>
          <button
            data-testid="cal-add-event-button"
            onClick={() => openCreate()}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
          >
            <Plus size={14} /> Neuer Termin
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {CATEGORIES.map(c => (
          <span key={c.value} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      <div className="bg-card border border-border p-3" data-testid="calendar-container">
        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-12">Laden...</div>
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={deLocale}
            firstDay={1}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            buttonText={{ today: "Heute", month: "Monat", week: "Woche", day: "Tag", list: "Liste" }}
            height="auto"
            events={fcEvents}
            editable
            droppable
            selectable
            selectMirror
            dayMaxEvents
            nowIndicator
            eventDrop={handleEventDrop}
            eventResize={handleEventDrop}
            eventClick={handleEventClick}
            select={handleSelect}
            eventDisplay="block"
            displayEventTime={true}
            displayEventEnd={false}
          />
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-card border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cal-event-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h2 className="text-lg font-bold">{editingId ? "Termin bearbeiten" : "Neuer Termin"}</h2>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Titel *</label>
                <input
                  data-testid="cal-form-title"
                  required value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Beschreibung</label>
                <textarea
                  value={form.description} rows={3}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-y"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox" id="all_day" checked={form.all_day}
                  onChange={e => setForm({ ...form, all_day: e.target.checked })}
                />
                <label htmlFor="all_day" className="text-sm">Ganztägig</label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Beginn *</label>
                  <input
                    data-testid="cal-form-start"
                    required type={form.all_day ? "date" : "datetime-local"}
                    value={form.all_day ? form.start.split("T")[0] : form.start}
                    onChange={e => setForm({ ...form, start: form.all_day ? `${e.target.value}T00:00` : e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Ende</label>
                  <input
                    type={form.all_day ? "date" : "datetime-local"}
                    value={form.all_day ? (form.end || "").split("T")[0] : form.end}
                    onChange={e => setForm({ ...form, end: form.all_day ? `${e.target.value}T23:59` : e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1 flex items-center gap-1"><Tag size={11} /> Kategorie</label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1 flex items-center gap-1"><Repeat size={11} /> Wiederholung</label>
                  <select
                    value={form.recurrence}
                    onChange={e => setForm({ ...form, recurrence: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  >
                    {RECURRENCE.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1 flex items-center gap-1"><MapPin size={11} /> Ort</label>
                <input
                  value={form.location}
                  onChange={e => setForm({ ...form, location: e.target.value })}
                  placeholder="Adresse, Online-Link, oder Raum"
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1 flex items-center gap-1"><CalIcon size={11} /> Kunde (optional)</label>
                <select
                  data-testid="cal-form-customer"
                  value={form.customer_id}
                  onChange={e => setForm({ ...form, customer_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                >
                  <option value="">— Kein Kundenbezug —</option>
                  {customers.map(c => (
                    <option key={c._id} value={c._id}>
                      {c.customer_number} — {c.company || `${c.first_name} ${c.last_name}`}
                    </option>
                  ))}
                </select>
                {form.customer_id && (
                  <button type="button" onClick={() => navigate(`/customers/${form.customer_id}`)}
                    className="mt-1 text-xs text-primary flex items-center gap-1 hover:underline">
                    Zum Kunden <ExternalLink size={10} />
                  </button>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1 flex items-center gap-1"><Users size={11} /> Teilnehmer</label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-background border border-input">
                  {users.map(u => {
                    const active = form.attendees.includes(u._id);
                    return (
                      <button
                        type="button" key={u._id}
                        onClick={() => setForm({
                          ...form,
                          attendees: active ? form.attendees.filter(a => a !== u._id) : [...form.attendees, u._id],
                        })}
                        className={`flex items-center gap-1 px-2 py-1 text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}
                      >
                        <span className="initials-avatar w-4 h-4 text-[10px]">{u.initials}</span>
                        {u.name}
                      </button>
                    );
                  })}
                  {users.length === 0 && <span className="text-xs text-muted-foreground">Keine Mitarbeiter verfügbar</span>}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="submit" data-testid="cal-form-submit"
                  className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
                  {editingId ? "Speichern" : "Termin erstellen"}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 bg-secondary text-sm font-semibold">Abbrechen</button>
                {editingId && (
                  <button type="button" onClick={remove}
                    className="ml-auto flex items-center gap-1 px-4 py-2 text-destructive hover:bg-destructive/10 text-sm font-semibold">
                    <Trash2 size={13} /> Löschen
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function toLocalInput(d) {
  // Returns YYYY-MM-DDTHH:mm in local tz for <input type="datetime-local">
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
